import { trait } from '@traits-ts/core'
import { useNotificationCreator } from '../../../composables/useNotificationCreator.js'
import { useMembersStore } from '../../../stores/members.js'

/**
 * WithNotifications trait
 * Adds notification capabilities to task classes
 */
export const WithNotifications = trait(superclass => class extends superclass {
  /**
   * Keep track of notification settings for this task
   */
  notificationSettings = {
    notifyOnAssignment: true,
    notifyOnStatusChange: true,
    notifyOnCompletion: true,
    notifyOnDeadlineApproaching: true,
    emailNotifications: true,
  }

  /**
   * Send a notification when this task is created
   * @param recipientId ID of the notification recipient
   * @returns The created notification ID
   */
  async notifyCreated(recipientId: string): Promise<string | null> {
    // Check if notifications are suppressed for this task
    if (this.metadata?.suppressCreationNotification) {
      return null
    }

    try {
      const notificationCreator = useNotificationCreator()
      const title = `New task: ${this.title}`
      const body = this.description || 'A new task has been assigned to you.'
      const priority = this.priority === 'critical' ? 'high' : 'normal'
      const metadata = {
        taskId: this.id,
        taskType: this.taskType,
        action: 'created',
      }

      const notificationId = await notificationCreator.createTaskNotification(
        recipientId,
        this.id,
        title,
        body,
        {
          priority,
          metadata,
          sendEmail: this.notificationSettings.emailNotifications,
        },
      )

      return notificationId
    }
    catch (error) {
      console.error('Error creating task notification:', error)
      return null
    }
  }

  /**
   * Send a notification when this task's status changes
   * @param recipientId ID of the notification recipient
   * @param oldStatus Previous task status
   * @param newStatus New task status
   * @returns The created notification ID
   */
  async notifyStatusChanged(
    recipientId: string,
    oldStatus: string,
    newStatus: string,
  ): Promise<string | null> {
    if (!this.notificationSettings.notifyOnStatusChange) {
      return null
    }

    try {
      const notificationCreator = useNotificationCreator()
      const title = `Task status updated: ${this.title}`
      const body = `This task has been changed from ${oldStatus} to ${newStatus}.`
      const priority = newStatus === 'completed' ? 'high' : 'normal'
      const metadata = {
        taskId: this.id,
        taskType: this.taskType,
        action: 'status_changed',
        oldStatus,
        newStatus,
      }

      // Special handling for completion notifications
      const sendEmail = newStatus === 'completed'
        ? this.notificationSettings.notifyOnCompletion && this.notificationSettings.emailNotifications
        : this.notificationSettings.emailNotifications

      const notificationId = await notificationCreator.createTaskNotification(
        recipientId,
        this.id,
        title,
        body,
        {
          priority,
          metadata,
          sendEmail,
        },
      )

      return notificationId
    }
    catch (error) {
      console.error('Error creating status change notification:', error)
      return null
    }
  }

  /**
   * Send a notification when this task is assigned to someone
   * @param recipientId ID of the notification recipient
   * @param assigneeId ID of the entity assigned to the task
   * @param assigneeType Type of entity assigned (member or subkrewe)
   * @returns The created notification ID
   */
  async notifyAssigned(
    recipientId: string,
    assigneeId: string,
    assigneeType: string,
  ): Promise<string | null> {
    if (!this.notificationSettings.notifyOnAssignment) {
      return null
    }

    try {
      const notificationCreator = useNotificationCreator()
      const title = `Task assigned: ${this.title}`
      const body = assigneeId === recipientId
        ? 'You have been assigned to this task.'
        : `This task has been assigned to ${assigneeType} ${assigneeId}.`
      const priority = this.priority === 'critical' ? 'high' : 'normal'
      const metadata = {
        taskId: this.id,
        taskType: this.taskType,
        action: 'assigned',
        assigneeId,
        assigneeType,
      }

      // Only send email if this is the person being assigned and email setting is enabled
      const sendEmail = assigneeId === recipientId
        ? this.notificationSettings.emailNotifications
        : false

      const notificationId = await notificationCreator.createTaskNotification(
        recipientId,
        this.id,
        title,
        body,
        {
          priority,
          metadata,
          sendEmail,
        },
      )

      return notificationId
    }
    catch (error) {
      console.error('Error creating assignment notification:', error)
      return null
    }
  }

  /**
   * Send a notification when this task's deadline is approaching
   * @param recipientId ID of the notification recipient
   * @param daysRemaining Number of days until the deadline
   * @returns The created notification ID
   */
  async notifyDeadlineApproaching(
    recipientId: string,
    daysRemaining: number,
  ): Promise<string | null> {
    if (!this.notificationSettings.notifyOnDeadlineApproaching) {
      return null
    }

    try {
      const notificationCreator = useNotificationCreator()
      const title = `Deadline approaching: ${this.title}`
      const body = `This task is due in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}.`
      const priority = daysRemaining <= 1 ? 'high' : 'normal'
      const metadata = {
        taskId: this.id,
        taskType: this.taskType,
        action: 'deadline_approaching',
        daysRemaining,
      }

      // For imminent deadlines, consider sending an email
      const sendEmail = daysRemaining <= 1
        ? this.notificationSettings.emailNotifications
        : false

      const notificationId = await notificationCreator.createTaskNotification(
        recipientId,
        this.id,
        title,
        body,
        {
          priority,
          metadata,
          sendEmail,
        },
      )

      return notificationId
    }
    catch (error) {
      console.error('Error creating deadline notification:', error)
      return null
    }
  }

  /**
   * Configure notification settings for this task
   * @param settings Settings to apply
   */
  configureNotifications(settings: Partial<typeof this.notificationSettings>): void {
    this.notificationSettings = {
      ...this.notificationSettings,
      ...settings,
    }
  }

  /**
   * Get member details from the member store
   * @param memberId Member ID
   * @returns Member details if found
   */
  async getMemberDetails(memberId: string): Promise<{ email: string, name: string } | null> {
    try {
      const membersStore = useMembersStore()
      const member = await membersStore.fetchMember(memberId)

      if (!member) {
        return null
      }

      return {
        email: member.email,
        name: member.name,
      }
    }
    catch (error) {
      console.error('Error fetching member details:', error)
      return null
    }
  }
})

// Add type interface for the trait
export interface WithNotifications {
  notificationSettings: {
    notifyOnAssignment: boolean
    notifyOnStatusChange: boolean
    notifyOnCompletion: boolean
    notifyOnDeadlineApproaching: boolean
    emailNotifications: boolean
  }

  notifyCreated(recipientId: string): Promise<string | null>
  notifyStatusChanged(recipientId: string, oldStatus: string, newStatus: string): Promise<string | null>
  notifyAssigned(recipientId: string, assigneeId: string, assigneeType: string): Promise<string | null>
  notifyDeadlineApproaching(recipientId: string, daysRemaining: number): Promise<string | null>
  configureNotifications(settings: Partial<{
    notifyOnAssignment: boolean
    notifyOnStatusChange: boolean
    notifyOnCompletion: boolean
    notifyOnDeadlineApproaching: boolean
    emailNotifications: boolean
  }>): void
  getMemberDetails(memberId: string): Promise<{ email: string, name: string } | null>
}
